import React, { useState } from 'react';
import { useInView } from '../useInView';
import { FaCheckCircle } from 'react-icons/fa';
import { MdFormatQuote } from 'react-icons/md';

const PRODUCTS = [
  {
    id: 2, badge: 'POPULAR', badgeColor: '#2e7d52',
    img: '/images/nonwoven-dcut-1.png',
    title: 'Non-Woven Bags',
    desc: 'non-woven bags with premium finish. Available in D-Cut, W-Cut and loop handle styles.',
    specs: ['D-Cut / W-Cut', 'Min 1000 Pcs', 'Govt. Approved'],
  },
  {
    id: 5, badge: 'NEW', badgeColor: '#c0392b',
    img: '/images/plastic-carry-1.png',
    title: 'Printed Plastic Carry Bags',
    desc: 'Custom printed plastic carry bags for any shop. Full-color logo, address and design on every piece.',
    specs: ['Full Colour', 'Logo Print', 'Min 1000 Pcs', 'Fast Turnaround'],
  },
  {
    id: 7, badge: 'TRENDING', badgeColor: '#c8893a',
    img: '/images/laminated-shop-1.png',
    imgHover: '/images/laminated-shop-2.png',
    title: 'Shopping Bags',
    desc: 'Premium shopping bags with sharp branding and smooth premium finish for daily retail use.',
    specs: ['Custom Print', 'Premium Finish', 'Min 1000 Pcs', 'All Sizes'],
  },
  {
    id: 8, badge: 'PREMIUM', badgeColor: '#c8893a',
    img: '/images/glossy-gold-bag-1.jpg',
    imgHover: '/images/glossy-gold-bag-2.jpg',
    title: 'Glossy Gold Printed Shining Bags',
    desc: 'Premium glossy gold foil printed non-woven bags specially designed for boutiques, jewellery stores, ethnic wear stores, gift shops, and premium retail branding purposes. These bags provide a luxurious and premium look that enhances brand visibility.',
    specs: ['Glossy Finish', 'Gold Foil Print', 'Custom Logo', 'Premium Branding'],
    altImg: 'Glossy Gold Printed Shining Bag',
    altImgHover: 'Premium Gold Foil Printed Non-Woven Bag',
    seoTitle: 'Glossy Gold Printed Shining Bags | Laxmi Printers Hyderabad',
    seoDesc: 'Premium glossy gold printed non-woven bags for boutiques, jewellery stores, and retail branding. Custom printing available at Laxmi Printers Hyderabad.'
  },
  {
    id: 9, badge: 'LUXURY', badgeColor: '#8e44ad',
    img: '/images/metallic-lamination-bag-1.jpg',
    imgHover: '/images/metallic-lamination-bag-2.jpg',
    title: 'Customized Metallic Bags',
    desc: 'Customized Metallic Bags are premium quality metallic non-woven bags specially manufactured for luxury branding and retail businesses. These bags provide an elegant glossy finish with high-quality customized printing for businesses that want a premium and professional appearance.',
    specs: ['Metallic Finish', 'Custom Logo', 'Multiple Colours', 'Water Resistant'],
    altImg: 'Customized Metallic Bag',
    altImgHover: 'Premium Metallic Carry Bag',
    seoTitle: 'Customized Metallic Bags | Laxmi Printers Hyderabad',
    seoDesc: 'Premium Customized Metallic Bags with customized logo printing and metallic glossy finish. Available in multiple colours and sizes at Laxmi Printers Hyderabad.'
  },
];

export default function Products() {
  const [hovered, setHovered] = useState(null);
  const [secRef, secV] = useInView();

  const scrollToContact = () => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <>
      <style>{`
        @keyframes cardReveal {
          from { opacity:0; transform:translateY(32px); }
          to { opacity:1; transform:translateY(0); }
        }
        .prod-card { transition: transform 0.3s ease, box-shadow 0.3s ease !important; }
        .prod-card:hover { transform: translateY(-8px) !important; box-shadow: 0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(200,137,58,0.2) !important; }
        .prod-cta:hover { background: linear-gradient(135deg,#c8893a,#e8a84a) !important; color: #0a0a0a !important; }
        @media(max-width:900px){ .prod-grid { grid-template-columns: repeat(2,1fr) !important; } }
        @media(max-width:580px){ .prod-grid { grid-template-columns: 1fr !important; } }
        @media(max-width:480px){
          .prod-title { font-size:clamp(28px,8vw,42px)!important; }
          .prod-desc { font-size:15px !important; line-height:1.6 !important; }
          .prod-card { padding:20px !important; }
        }
      `}</style>
      <section id="products" style={s.section}>
        {/* Background texture */}
        <div style={s.bgStripe} />

        <div style={s.container}>
          {/* Header */}
          <div ref={secRef} style={{ ...s.header, opacity: secV ? 1 : 0, transform: secV ? 'none' : 'translateY(24px)', transition: 'all 0.7s ease' }}>
            <div style={s.tagLine}><span style={s.dash} /><span style={s.tag}>OUR PRODUCTS</span><span style={s.dash} /></div>
            <h2 className="prod-title" style={s.title}>Premium Quality<br /><em style={s.titleItalic}>Bags & Covers</em></h2>
            <p className="prod-desc" style={s.subtitle}>Directly manufactured — available in all sizes, colours & custom prints for your shop or brand.</p>
          </div>

          {/* Grid */}
          <div className="prod-grid" style={s.grid}>
            {PRODUCTS.map((p, i) => (
              <div
                key={p.id}
                className="prod-card"
                style={{
                  ...s.card,
                  animation: secV ? `cardReveal 0.6s ease ${i * 0.09}s both` : 'none',
                }}
                onMouseEnter={() => setHovered(p.id)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* Badge */}
                <div style={{ ...s.badge, background: p.badgeColor }}>{p.badge}</div>

                {/* Image */}
                <div style={s.imgWrap}>
                  <img
                    src={hovered === p.id && p.imgHover ? p.imgHover : p.img}
                    alt={hovered === p.id && p.imgHover ? (p.altImgHover || p.title) : (p.altImg || p.title)}
                    title={p.seoTitle || p.title}
                    style={{ ...s.img, transform: hovered === p.id ? 'scale(1.07)' : 'scale(1)' }}
                    loading="lazy"
                  />
                  <div style={s.imgOverlay} />
                </div>
                {p.seoDesc && <span style={{ display: 'none' }}>{p.seoDesc}</span>}

                {/* Body */}
                <div style={s.body}>
                  <h3 style={s.cardTitle}>{p.title}</h3>
                  <p style={s.cardDesc}>{p.desc}</p>

                  <div style={s.specRow}>
                    {p.specs.map(sp => (
                      <span key={sp} style={s.spec}>
                        <FaCheckCircle style={{ marginRight: 4, marginBottom: -2 }} />
                        {sp}
                      </span>
                    ))}
                  </div>

                  <button className="prod-cta" style={s.cta} onClick={scrollToContact}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                      <MdFormatQuote style={{ fontSize: 16 }} /> Get Price Quote
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

const s = {
  section: { background: '#0a0a0a', padding: '120px 24px', position: 'relative', overflow: 'hidden' },
  bgStripe: {
    position: 'absolute', top: 0, right: 0, width: '40%', height: '100%',
    background: 'radial-gradient(ellipse at right center, rgba(200,137,58,0.04) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
  container: { maxWidth: 1280, margin: '0 auto' },
  header: { textAlign: 'center', marginBottom: 64 },
  tagLine: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 16 },
  dash: { width: 40, height: 1, background: 'rgba(200,137,58,0.5)', display: 'inline-block' },
  tag: { fontSize: 11, letterSpacing: 4, color: '#c8893a', fontWeight: 700 },
  title: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 'clamp(32px,4vw,54px)', fontWeight: 700,
    color: '#f0ede6', lineHeight: 1.15, marginBottom: 16,
  },
  titleItalic: { fontStyle: 'italic', color: '#c8893a' },
  subtitle: { fontSize: 15.5, color: 'rgba(240,237,230,0.5)', maxWidth: 500, margin: '0 auto' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 },
  card: {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 20, overflow: 'hidden', position: 'relative',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  },
  badge: {
    position: 'absolute', top: 16, left: 16, zIndex: 10,
    fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
    color: '#fff', padding: '4px 10px', borderRadius: 6,
  },
  imgWrap: { position: 'relative', overflow: 'hidden', height: 220 },
  img: { width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease' },
  imgOverlay: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(to bottom, transparent 40%, rgba(10,10,10,0.85))',
  },
  body: { padding: '22px 22px 24px' },
  cardTitle: {
    fontFamily: "'Cormorant Garamond', serif", fontSize: 21, fontWeight: 700,
    color: '#f0ede6', marginBottom: 10, lineHeight: 1.2,
  },
  cardDesc: { fontSize: 13.5, color: 'rgba(240,237,230,0.55)', lineHeight: 1.7, marginBottom: 16 },
  specRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 },
  spec: {
    fontSize: 11, fontWeight: 600, color: 'rgba(200,137,58,0.85)',
    background: 'rgba(200,137,58,0.1)', border: '1px solid rgba(200,137,58,0.2)',
    padding: '3px 9px', borderRadius: 5, letterSpacing: 0.3,
  },
  cta: {
    width: '100%', background: 'rgba(200,137,58,0.1)',
    border: '1px solid rgba(200,137,58,0.3)', color: '#c8893a',
    borderRadius: 10, padding: '12px', fontFamily: "'Outfit', sans-serif",
    fontWeight: 700, fontSize: 14, cursor: 'pointer', transition: 'all 0.25s ease',
    letterSpacing: 0.3,
  },
};
